import type { VehicleDTO } from "@/api/vtnz-backend/vehicles";
import { VehicleCore, type VehicleCoreOptions } from "../core/vehicleCoreClass";
import type { FuelType, Vehicle, VehicleSource } from "../types";

type VehicleICEFuelType = Extract<FuelType, "PETROL" | "DIESEL" | "HYBRID">;

type VehicleICESource = Extract<VehicleSource, "TELEMATICS" | "CUSTOM">;

type VehicleICEOptions = Omit<
  VehicleCoreOptions,
  "fuelType" | "vehicleSource"
> & {
  fuelType: VehicleICEFuelType;
  vehicleSource: VehicleICESource;
  /** Foreign key into `epa_fuel_efficiency_ratings.id`, if resolved. */
  epaFuelEfficiencyId?: string;
};

export class VehicleICE extends VehicleCore {
  /**
   * Foreign key into `epa_fuel_efficiency_ratings.id` - the EPA fuel
   * efficiency/emissions profile matched to this vehicle's make/model/year,
   * if one has been resolved. ICE-only: that table is ~51k rows with no
   * server-side pagination, so it's never listed wholesale; see
   * vehicleStore.fetchEpaFuelEfficiencyRatings for the batched by-id fetch.
   */
  epaFuelEfficiencyId: string | null;

  constructor({
    externalId,
    imageSrcPaths,
    licensePlate,
    fuelType,
    vehicleSource,
    name,
    valhallaCostingType,
    createdAt,
    createdBy,
    applications,
    aux,
    compCap,
    epaFuelEfficiencyId,
    homeLocationExternalId,
    gvm,
    lpwdRequired,
    maxAirPressure,
    numberOfAxles,
    pressure35Ratio,
    registrationExpiry,
    tractorTrailerRegistration,
    tslRequired,
    vehicleClass,
    vin,
    vinLocation,
    wofCofExpiry,
    wofCofNumber,
  }: VehicleICEOptions) {
    super({
      externalId,
      imageSrcPaths,
      licensePlate,
      fuelType,
      vehicleSource,
      name,
      valhallaCostingType,
      createdAt,
      createdBy,
      applications,
      aux,
      compCap,
      homeLocationExternalId,
      gvm,
      lpwdRequired,
      maxAirPressure,
      numberOfAxles,
      pressure35Ratio,
      registrationExpiry,
      tractorTrailerRegistration,
      tslRequired,
      vehicleClass,
      vin,
      vinLocation,
      wofCofExpiry,
      wofCofNumber,
    });

    this.epaFuelEfficiencyId = epaFuelEfficiencyId ?? null;
  }

  static fromDTO(dto: VehicleDTO): Vehicle {
    if (!dto.fuel_type) throw new Error("No fuel type provided");
    if (dto.fuel_type !== "DIESEL" && dto.fuel_type !== "PETROL" && dto.fuel_type !== "HYBRID")
      throw new Error("Unsupported fuel type for this vehicle subclass");

    // A read returns every column, so a column that is null in the database
    // arrives as `null`. The domain model uses `undefined` for an absent
    // value, and this is the boundary that converts between them.
    return new VehicleICE({
      fuelType: dto.fuel_type,
      vehicleSource: "CUSTOM",
      externalId: dto.id,
      valhallaCostingType:
        dto.vehicle_class === "Heavy Truck" ||
        dto.vehicle_class === "Medium Commercial"
          ? "truck"
          : "auto",
      vin: dto.vin ?? undefined,
      vinLocation: dto.vin_location ?? undefined,
      licensePlate: dto.rego ?? undefined,
      name: dto.name ?? undefined,
      createdAt: dto.created_at,
      createdBy: dto.created_by,
      applications: dto.applications ?? undefined,
      epaFuelEfficiencyId: dto.epa_fuel_efficiency_id ?? undefined,
      homeLocationExternalId: dto.home_location_id ?? undefined,
      aux: dto.aux ?? undefined,
      compCap: dto.comp_cap ?? undefined,
      gvm: dto.gvm ?? undefined,
      lpwdRequired: dto.lpwd_required ?? undefined,
      maxAirPressure: dto.max_air_pressure ?? undefined,
      numberOfAxles: dto.number_of_axles ?? undefined,
      pressure35Ratio: dto.pressure_3_5_ratio ?? undefined,
      registrationExpiry: dto.registration_expiry ?? undefined,
      tractorTrailerRegistration: dto.tractor_trailer_registration ?? undefined,
      tslRequired: dto.tsl_required ?? undefined,
      vehicleClass: dto.vehicle_class ?? undefined,
      wofCofExpiry: dto.wof_cof_expiry ?? undefined,
      wofCofNumber: dto.wof_cof_number ?? undefined,
    });
  }

  // ----------------------------------------------------------------------- //
  // -------------------------------- Getters ------------------------------ //
  // ----------------------------------------------------------------------- //

  /**
   * Gets the fuel capacity display text based on the fuel type.
   * @returns The fuel capacity display text. Unknown litres for diesel/petrol/hydrogen, kWh for electric/hybrid/plug in hybrid, empty string for unknown.
   */
  public get fuelCapacityDisplayText() {
    switch (this.fuelType) {
      case "DIESEL":
      case "PETROL":
      case "HYBRID":
      default:
        return null;
    }
  }
}
